import { Head, router } from "@inertiajs/react"
import {
  CircleAlert,
  Coffee,
  Droplets,
  EllipsisVertical,
  type LucideIcon,
  Pencil,
  Smile,
  Tag,
  Trash2,
  Utensils,
  Zap,
} from "lucide-react"
import { toast } from "sonner"

import type { ServerEntry } from "@/components/entry-detail-sheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import { entriesPath, entryPath, restoreEntryPath } from "@/routes"

// ── Config ────────────────────────────────────────────────────────────────────

const entryTypeConfig: Record<
  string,
  { icon: LucideIcon; bgVar: string; iconVar: string }
> = {
  symptom: {
    icon: CircleAlert,
    bgVar: "--fig-entry-symptom-bg",
    iconVar: "--fig-entry-symptom-icon",
  },
  meal: {
    icon: Utensils,
    bgVar: "--fig-entry-meal-bg",
    iconVar: "--fig-entry-meal-icon",
  },
  mood: {
    icon: Smile,
    bgVar: "--fig-entry-mood-bg",
    iconVar: "--fig-entry-mood-icon",
  },
  energy: {
    icon: Zap,
    bgVar: "--fig-entry-mood-bg",
    iconVar: "--fig-entry-mood-icon",
  },
  drink: {
    icon: Coffee,
    bgVar: "--fig-entry-beverage-bg",
    iconVar: "--fig-entry-beverage-icon",
  },
  water: {
    icon: Droplets,
    bgVar: "--fig-entry-water-bg",
    iconVar: "--fig-entry-water-icon",
  },
  stool: {
    icon: Droplets,
    bgVar: "--fig-entry-stool-bg",
    iconVar: "--fig-entry-stool-icon",
  },
}

const moodOptions = [
  { level: 1, emoji: "😫", label: "Awful" },
  { level: 2, emoji: "😕", label: "Low" },
  { level: 3, emoji: "😐", label: "Okay" },
  { level: 4, emoji: "🙂", label: "Good" },
  { level: 5, emoji: "😊", label: "Great" },
]

const energyOptions = [
  { level: 1, emoji: "🪫", label: "Drained" },
  { level: 2, emoji: "😴", label: "Low" },
  { level: 3, emoji: "⚡", label: "Moderate" },
  { level: 4, emoji: "💪", label: "High" },
  { level: 5, emoji: "🔥", label: "Wired" },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function entryTitle(entry: ServerEntry): string {
  switch (entry.kind) {
    case "symptom": {
      const t = entry.data.symptom_type as string | undefined
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Symptom"
    }
    case "meal": {
      const t = entry.data.meal_type as string | undefined
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Meal"
    }
    case "drink": {
      const t = entry.data.drink_type as string | undefined
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Drink"
    }
    case "stool":
      return "Stool"
    case "mood":
      return "Mood check-in"
    case "energy":
      return "Energy check-in"
    default:
      return entry.kind.charAt(0).toUpperCase() + entry.kind.slice(1)
  }
}

function resolvedConfig(entry: ServerEntry) {
  if (entry.kind === "drink") {
    const dt = entry.data.drink_type as string | undefined
    if (dt === "water") return entryTypeConfig.water
    return entryTypeConfig.drink
  }
  return entryTypeConfig[entry.kind] ?? entryTypeConfig.meal
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ShowProps {
  entry: ServerEntry
}

export default function Show({ entry }: ShowProps) {
  const config = resolvedConfig(entry)
  const Icon = config.icon
  const title = entryTitle(entry)

  const handleEdit = () => {
    // Navigate to the entries path with edit — for now redirect to dashboard
    // Full inline editing is handled via the dashboard bottom sheet
    router.visit(entriesPath())
  }

  const handleDelete = () => {
    router.delete(entryPath(entry.id), {
      onSuccess: () => {
        toast("Entry deleted", {
          action: {
            label: "Undo",
            onClick: () => {
              router.post(
                restoreEntryPath(entry.id),
                {},
                {
                  preserveScroll: true,
                  onSuccess: () => toast("Entry restored", { duration: 2000 }),
                },
              )
            },
          },
          duration: 5000,
        })
        router.visit(entriesPath())
      },
    })
  }

  const moodOrEnergy =
    entry.kind === "mood" || entry.kind === "energy"
      ? (entry.kind === "mood" ? moodOptions : energyOptions).find(
          (o) => o.level === (entry.data.level as number | undefined),
        )
      : null

  return (
    <AppLayout>
      <Head title={title} />

      <div className="px-5 pt-5" style={{ paddingBottom: 88 }}>
        {/* Header card */}
        <div
          className="mb-5 flex items-center gap-3 rounded-2xl p-4"
          style={{
            background: "var(--fig-card-bg)",
            boxShadow: "var(--fig-card-shadow)",
            border: "1px solid var(--fig-card-border)",
          }}
        >
          <div
            className="flex shrink-0 items-center justify-center"
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: `var(${config.bgVar})`,
            }}
          >
            <Icon size={28} style={{ color: `var(${config.iconVar})` }} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 style={{ fontSize: 20, fontWeight: 700 }}>{title}</h1>
            <span style={{ fontSize: 13, color: "var(--fig-text-inactive)" }}>
              {new Date(entry.occurred_at).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          </div>
        </div>

        {/* Detail sections */}
        <div
          className="mb-5 rounded-2xl p-4"
          style={{
            background: "var(--fig-card-bg)",
            boxShadow: "var(--fig-card-shadow)",
            border: "1px solid var(--fig-card-border)",
          }}
        >
          {/* Kind-specific */}
          {entry.kind === "symptom" && (
            <div className="mb-4">
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Symptom Details
              </p>
              <div className="flex flex-wrap gap-2">
                {typeof entry.data.symptom_type === "string" &&
                  entry.data.symptom_type && (
                    <span
                      className="rounded-lg px-3 py-1.5"
                      style={{
                        fontSize: 14,
                        background: "var(--fig-entry-symptom-bg)",
                        color: "var(--fig-entry-symptom-icon)",
                        fontWeight: 500,
                      }}
                    >
                      {entry.data.symptom_type}
                    </span>
                  )}
                {entry.data.severity !== undefined && (
                  <span
                    className="rounded-lg px-3 py-1.5"
                    style={{
                      fontSize: 14,
                      background: "var(--fig-detail-section-bg)",
                      color: "var(--fig-text-primary)",
                      fontWeight: 500,
                    }}
                  >
                    {entry.data.severity as number}/5 severity
                  </span>
                )}
              </div>
            </div>
          )}

          {entry.kind === "stool" && (
            <div className="mb-4">
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Bristol Type
              </p>
              <span
                className="rounded-lg px-3 py-1.5"
                style={{
                  fontSize: 14,
                  background: "var(--fig-detail-section-bg)",
                  color: "var(--fig-text-primary)",
                  fontWeight: 500,
                  display: "inline-block",
                }}
              >
                Type {(entry.data.bristol_type as number | undefined) ?? "—"}
                {entry.data.description
                  ? ` — ${entry.data.description as string}`
                  : ""}
              </span>
            </div>
          )}

          {(entry.kind === "mood" || entry.kind === "energy") &&
            moodOrEnergy && (
              <div className="mb-4">
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--fig-text-inactive)",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  {entry.kind === "mood" ? "Mood" : "Energy"} Level
                </p>
                <span
                  className="rounded-lg px-3 py-1.5"
                  style={{
                    fontSize: 14,
                    background: "var(--fig-detail-section-bg)",
                    color: "var(--fig-text-primary)",
                    fontWeight: 500,
                    display: "inline-block",
                  }}
                >
                  {moodOrEnergy.emoji} {moodOrEnergy.label} (
                  {entry.data.level as number}/5)
                </span>
              </div>
            )}

          {/* Tags */}
          {entry.tags && entry.tags.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 flex items-center gap-2">
                <Tag size={13} style={{ color: "var(--fig-text-inactive)" }} />
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--fig-text-inactive)",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  Tags
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {entry.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded-lg px-2.5 py-1"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      background: "var(--fig-tag-bg)",
                      color: "var(--fig-tag-text)",
                    }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {entry.notes && (
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--fig-text-inactive)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Notes
              </p>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "var(--fig-text-secondary)",
                }}
              >
                {entry.notes}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleEdit}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "12px 0",
              borderRadius: 14,
              background: "var(--fig-fab-bg)",
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
            }}
          >
            <Pencil size={16} aria-hidden="true" />
            Edit
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More options"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "var(--fig-tag-bg)",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--fig-text-secondary)",
                  flexShrink: 0,
                }}
              >
                <EllipsisVertical size={18} aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem variant="destructive" onClick={handleDelete}>
                <Trash2 size={15} aria-hidden="true" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </AppLayout>
  )
}
